import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Search, Plus, Download, Package, Gem, Heart, Bell, Layers, BookOpen, Construction, Gift, BellRing, Bookmark, BookmarkCheck, Shuffle as ShuffleIcon } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { BloodDrip } from "@/components/BloodDrip";
import { SkullRating } from "@/components/SkullRating";
import ProductModal from "@/components/ProductModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "all", label: "All Items" },
  { id: "physical", label: "Physical" },
  { id: "digital", label: "Digital" },
  { id: "collectible", label: "Collectibles" },
  { id: "bundle", label: "Bundles" },
  { id: "giftcard", label: "Gift Cards" },
  { id: "wishlist", label: "Want-List" },
];

const FORMAT_ICON = { physical: Package, digital: Download, collectible: Gem, bundle: Layers, giftcard: Gift, mystery: ShuffleIcon };

const seriesKey = (t) => t.split(/\s+#|\s+—|\s+:/)[0].trim().toLowerCase();

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [storeOpen, setStoreOpen] = useState(true);
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("featured");
  const [active, setActive] = useState(null);
  const { add, setOpen } = useCart();
  const { user, toggleWish, toggleSeries, toggleStock, togglePull } = useAuth();

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => setProducts(r.data))
      .catch(() => toast.error("Couldn't load the rack. Try reloading."));
    axios
      .get(`${API}/store-status`)
      .then((r) => setStoreOpen(r.data.open))
      .catch(() => {});
  }, []);

  const visible = useMemo(() => {
    let list = products.filter(
      (p) =>
        (tab === "wishlist"
          ? (user?.wishlist || []).includes(p.id)
          : tab === "all" || p.category === tab || (tab === "physical" && p.category === "mystery")) &&
        p.title.toLowerCase().includes(query.toLowerCase())
    );
    if (sort === "low") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "high") list = [...list].sort((a, b) => b.price - a.price);
    return list;
  }, [products, tab, query, sort, user]);

  const heart = (p) => {
    if (!user) {
      toast.error("Log in to build your want-list.", {
        action: { label: "Log In", onClick: () => (window.location.href = "/account") },
      });
      return;
    }
    toggleWish(p.id).then((added) =>
      toast.success(added ? `${p.title} joins your want-list.` : `${p.title} leaves your want-list.`)
    );
  };

  const bell = (p) => {
    if (!user) {
      toast.error("Log in to stalk a series.", {
        action: { label: "Log In", onClick: () => (window.location.href = "/account") },
      });
      return;
    }
    toggleSeries(seriesKey(p.title)).then((on) =>
      toast.success(on ? `You'll hear the moment a new ${seriesKey(p.title)} issue rises.` : `Series alert silenced.`)
    );
  };

  const restock = (p) => {
    if (!user) {
      toast.error("Log in to get restock alerts.", {
        action: { label: "Log In", onClick: () => (window.location.href = "/account") },
      });
      return;
    }
    toggleStock(p.id).then((on) =>
      toast.success(on ? `You'll be emailed the moment ${p.title} crawls back.` : `Restock alert off.`)
    );
  };

  const pull = (p) => {
    if (!user) {
      toast.error("Log in to start a pull list.", {
        action: { label: "Log In", onClick: () => (window.location.href = "/account") },
      });
      return;
    }
    togglePull(seriesKey(p.title)).then((on) =>
      toast.success(
        on
          ? `${seriesKey(p.title)} is on your pull list — new issues auto-reserve in your coffin.`
          : `Pull list dropped.`
      )
    );
  };

  return (
    <section id="shop" className="relative py-28 sm:py-36 newsprint-grain">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="font-marker text-crimson text-base sm:text-lg mb-3 -rotate-1 inline-block"
        >
          take her home. if you dare.
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase text-paper mb-12"
        >
          THE <span className="text-bloodglow">CRYPT SHOP</span>
          <BloodDrip />
        </motion.h2>

        {!storeOpen ? (
          <div data-testid="store-closed-message" className="border-2 border-blooddeep bg-pulp p-12 sm:p-16 text-center max-w-2xl mx-auto">
            <Construction size={44} className="mx-auto text-yell mb-6" />
            <h3 className="font-display text-2xl sm:text-3xl uppercase text-paper mb-4">
              UNDER <span className="text-crimson">CONSTRUCTION</span>
            </h3>
            <p className="font-type text-base text-paper/70">
              Store is currently under construction, please check back later.
            </p>
          </div>
        ) : (
          <>
        <div className="flex flex-col lg:flex-row lg:items-center gap-5 mb-12">
          <div className="flex flex-wrap gap-2" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`shop-category-tab-${t.id === "collectible" ? "collectibles" : t.id}`}
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={`font-impact uppercase tracking-wider text-xs px-4 py-2.5 border-2 border-black transition-all duration-150 ${
                  t.id === "wishlist" ? "flex items-center gap-1.5 " : ""
                }${
                  tab === t.id
                    ? "bg-crimson text-white ink-shadow-sm"
                    : "bg-charcoal text-paper/60 hover:text-paper hover:-translate-y-0.5"
                }`}
              >
                {t.id === "wishlist" && <Heart size={12} className={user && (user.wishlist || []).length ? "fill-current" : ""} />}
                {t.id === "wishlist" && user ? `Want-List (${(user.wishlist || []).length})` : t.label}
              </button>
            ))}
          </div>
          <div className="flex flex-1 gap-3 lg:justify-end">
            <div className="relative flex-1 lg:max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-paper/40" />
              <input
                data-testid="shop-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the crypt..."
                className="w-full font-courier text-sm bg-void border-2 border-input text-paper pl-9 pr-3 py-2.5 focus:outline-none focus:border-crimson focus:ring-1 focus:ring-crimson placeholder:text-paper/30"
              />
            </div>
            <select
              data-testid="shop-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="font-courier text-sm bg-void border-2 border-input text-paper px-3 py-2.5 focus:outline-none focus:border-crimson"
            >
              <option value="featured">Featured</option>
              <option value="low">Price: Low → High</option>
              <option value="high">Price: High → Low</option>
            </select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {visible.map((p, i) => {
            const Icon = FORMAT_ICON[p.category] || Package;
            const wished = (user?.wishlist || []).includes(p.id);
            const bellOn = (user?.series_alerts || []).includes(seriesKey(p.title));
            return (
              <motion.article
                key={p.id}
                data-testid="shop-product-card"
                layout
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
                className="group relative border-2 border-border bg-pulp transition-colors duration-200 hover:border-crimson flex flex-col"
              >
                <div className="relative overflow-hidden aspect-[2/3] bg-void cursor-pointer" onClick={() => setActive(p)}>
                  <img
                    src={p.cover}
                    alt={`${p.title} cover`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 halftone opacity-0 group-hover:opacity-20 transition-opacity duration-300 pointer-events-none" />
                  <span className="absolute top-3 -left-1 bg-blooddeep text-white font-marker text-[11px] px-2.5 py-0.5 -rotate-3 ink-shadow-sm">
                    {p.badge}
                  </span>
                  {p.preorder && (
                    <span data-testid={`preorder-tag-${p.id}`} className="absolute top-9 -left-1 bg-yell text-black font-marker text-[11px] px-2.5 py-0.5 rotate-2 ink-shadow-sm">
                      PRE-ORDER
                    </span>
                  )}
                  {p.was_price && p.was_price > p.price && !p.preorder && (
                    <span
                      data-testid={`sale-tag-${p.id}`}
                      className="absolute top-9 -left-1 bg-crimson text-white font-marker text-[11px] px-2.5 py-0.5 rotate-2 ink-shadow-sm"
                    >
                      ON SALE
                    </span>
                  )}
                  {p.preview_pages?.length > 0 && (
                    <span data-testid={`preview-tag-${p.id}`} className="absolute bottom-3 left-3 flex items-center gap-1 font-courier text-[10px] uppercase tracking-widest bg-void/85 text-yell px-2 py-1 border border-yell/40">
                      <BookOpen size={11} /> Free Preview
                    </span>
                  )}
                  <button
                    data-testid={`wishlist-toggle-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); heart(p); }}
                    className="absolute top-3 right-3 p-2 bg-void/85 border border-paper/20 transition-colors duration-150 hover:border-crimson"
                    aria-label={wished ? `Remove ${p.title} from want-list` : `Add ${p.title} to want-list`}
                  >
                    <Heart size={15} className={wished ? "fill-crimson text-crimson" : "text-paper/70"} />
                  </button>
                  <button
                    data-testid={`series-bell-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); bell(p); }}
                    className="absolute top-14 right-3 p-2 bg-void/85 border border-paper/20 transition-colors duration-150 hover:border-yell"
                    aria-label={`New-issue alerts for ${seriesKey(p.title)}`}
                    title="Email me when a new issue in this series drops"
                  >
                    <Bell size={15} className={bellOn ? "fill-yell text-yell" : "text-paper/70"} />
                  </button>
                  <button
                    data-testid={`pull-toggle-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); pull(p); }}
                    className="absolute top-[104px] right-3 p-2 bg-void/85 border border-paper/20 transition-colors duration-150 hover:border-cyanx"
                    aria-label={`Pull list for ${seriesKey(p.title)}`}
                    title="Pull list: new issues auto-reserve in your cart + email"
                  >
                    {(user?.pull_list || []).includes(seriesKey(p.title)) ? (
                      <BookmarkCheck size={15} className="fill-cyanx text-cyanx" />
                    ) : (
                      <Bookmark size={15} className="text-paper/70" />
                    )}
                  </button>
                  <span className="absolute bottom-3 right-3 flex items-center gap-1 font-courier text-[10px] uppercase tracking-widest bg-void/85 text-cyanx px-2 py-1 border border-cyanx/40">
                    <Icon size={11} /> {p.format}
                  </span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-impact uppercase tracking-wide text-lg text-paper cursor-pointer hover:text-crimson transition-colors" onClick={() => setActive(p)}>
                    {p.title}
                  </h3>
                  <div className="mt-1.5">
                    <SkullRating value={p.avg_rating || 0} count={p.review_count} size={12} testId={`rating-${p.id}`} />
                  </div>
                  <p className="font-type text-xs text-paper/55 mt-1.5 leading-relaxed flex-1">{p.description}</p>
                  {p.bundle_titles?.length > 0 && (
                    <p data-testid={`bundle-contents-${p.id}`} className="font-courier text-[10px] text-paper/45 mt-2 leading-relaxed">
                      INCLUDES: {p.bundle_titles.join(" + ")}
                    </p>
                  )}
                  {p.preorder && (
                    <p data-testid={`preorder-note-${p.id}`} className="font-courier text-[10px] uppercase tracking-widest text-yell/80 mt-2">
                      Pre-order{p.ships_by ? ` — ships by ${p.ships_by}` : ""}
                    </p>
                  )}
                  {p.stock != null && p.category !== "digital" && p.category !== "bundle" && (
                    <p className="mt-2">
                      {p.stock === 0 ? (
                        <>
                          <span data-testid={`soldout-${p.id}`} className="font-marker text-[11px] text-paper/40 border border-paper/30 px-2 py-0.5 rotate-2 inline-block">
                            SOLD OUT
                          </span>
                          <button
                            data-testid={`restock-notify-${p.id}`}
                            onClick={() => restock(p)}
                            className={`ml-2 inline-flex items-center gap-1 font-marker text-[11px] px-2 py-0.5 -rotate-1 border transition-colors duration-150 ${
                              (user?.stock_alerts || []).includes(p.id)
                                ? "bg-yell text-black border-black"
                                : "text-yell border-yell/50 hover:bg-yell/10"
                            }`}
                          >
                            <BellRing size={11} /> {(user?.stock_alerts || []).includes(p.id) ? "YOU'RE ON THE LIST" : "NOTIFY WHEN BACK"}
                          </button>
                        </>
                      ) : p.stock <= 5 ? (
                        <span data-testid={`lowstock-${p.id}`} className="font-marker text-[11px] bg-blooddeep text-white px-2 py-0.5 rotate-2 ink-shadow-sm inline-block">
                          ONLY {p.stock} LEFT
                        </span>
                      ) : null}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-5">
                    <span className="flex items-baseline gap-2">
                      {p.was_price && p.was_price > p.price && (
                        <span className="font-courier text-xs text-paper/40 line-through">${p.was_price.toFixed(2)}</span>
                      )}
                      <span className="font-marker text-yell text-xl rotate-[-2deg]">${p.price.toFixed(2)}</span>
                    </span>
                    <button
                      data-testid={`add-to-cart-button-${p.id}`}
                      disabled={p.stock === 0 && p.category !== "digital" && p.category !== "bundle"}
                      onClick={() => {
                        add(p);
                        toast.success(`${p.title} rests in your coffin.`, {
                          action: { label: "View Coffin", onClick: () => setOpen(true) },
                        });
                      }}
                      className="flex items-center gap-1.5 font-impact uppercase tracking-wider text-xs bg-crimson text-white border-2 border-black px-4 py-2.5 ink-shadow-sm transition-all duration-150 hover:bg-crimsonhot hover:-translate-y-0.5 active:translate-x-0.5 active:translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      <Plus size={14} /> {p.stock === 0 && p.category !== "digital" && p.category !== "bundle" ? "Sold Out" : p.preorder ? "Pre-Order" : "Add to Coffin"}
                    </button>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
        {visible.length === 0 && (
          <p data-testid="shop-empty-state" className="font-type text-paper/50 text-center py-16">
            {tab === "wishlist"
              ? "No hearts given yet. Tap the heart on any item to build your want-list."
              : "The crypt holds nothing by that name. Search again, if you dare."}
          </p>
        )}
          </>
        )}
      </div>
      <AnimatePresence>
        {active && <ProductModal product={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}
